import { Plus, Sparkles, Upload } from 'lucide-react';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { Card, CardContent } from './ui/card';
import { ImageWithFallback } from './ImageWithFallback';
import { Badge } from './ui/badge';
import { Pagination } from './shared/Pagination';
import { useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getUploadHistory } from '@/services/showroom';

interface RoomItem {
  id: number;
  imageUrl: string;
  resizedImageUrl: string;
  roomType: string;
  roomStyle: string;
  isPublic: boolean;
  createdAt: string;
  updatedAt: string;
  analysis: {
    items: Array<{ bbox: number[]; name: string }>;
    colors: {
      dominant: { hex: string; name: string };
      accent: { hex: string; name: string };
      secondary: { hex: string; name: string };
    };
    room_area: { width_ft: number; height_ft: number };
    room_type: string;
    room_style: string;
  };
}

interface UploadHistoryResponse {
  items: RoomItem[];
  meta: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
  };
}

interface ShowroomOnboardingProps {
  handleImageUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  isAnalyzing: boolean;
  analysisProgress: number;
  handleSelectFromHistory: (item: RoomItem) => void;
  handleUploadNew: () => void;
}

const ShowroomOnboarding: React.FC<ShowroomOnboardingProps> = ({
  handleImageUpload,
  isAnalyzing,
  analysisProgress,
  handleSelectFromHistory,
  handleUploadNew,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 8; // Number of items per page

  const { data, isLoading, isError } = useQuery<UploadHistoryResponse>({
    queryKey: ['templates', currentPage],
    queryFn: () => getUploadHistory(currentPage, pageSize),
  });

  const templates = data?.items || [];
  const totalPages = data?.meta?.totalPages || 1;

  const handlePageChange = (page: number) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSelect = (item: RoomItem) => {
    handleSelectFromHistory(item);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Clean Header */}
      <div className="border-b border-gray-100 bg-white"></div>

      {/* Upload Area */}
      <div className="flex-1 flex flex-col justify-center px-3 py-4 sm:px-4 sm:py-6 lg:p-8 bg-white">
        <div className="max-w-5xl w-full mx-auto">
          {/* Upload Section */}
          <div className="max-w-xl mx-auto mb-8 sm:mb-10 lg:mb-12">
            <div
              className="border-2 border-dashed border-gray-200 rounded-2xl p-6 sm:p-10 lg:p-16 text-center cursor-pointer hover:border-gray-300 transition-colors bg-gray-50/30"
              onClick={handleUploadNew}
            >
              <Upload className="h-10 w-10 sm:h-12 sm:w-12 lg:h-16 lg:w-16 mx-auto text-gray-400 mb-4 sm:mb-5" />
              <h3 className="text-lg sm:text-xl text-gray-900 mb-2 sm:mb-3">
                Upload your room image
              </h3>
              <p className="text-sm sm:text-base text-gray-500 mb-5 sm:mb-6">
                Drag and drop or click to select an image
              </p>
              <Button className="bg-black text-white hover:bg-gray-800">
                <Plus className="h-4 w-4 mr-2" />
                Select Image
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
              />
            </div>

            {/* Analysis Progress */}
            {isAnalyzing && (
              <div className="mt-6 sm:mt-8 space-y-3 sm:space-y-4">
                <div className="flex items-center gap-3 justify-center">
                  <Sparkles className="h-5 w-5 text-black animate-pulse" />
                  <span className="text-gray-900">Analyzing your space...</span>
                </div>
                <Progress value={analysisProgress} className="w-full h-2" />
                <p className="text-center text-sm text-gray-500">
                  AI is detecting furniture, colors, and style patterns
                </p>
              </div>
            )}
          </div>

          {/* Divider */}
          <div className="flex items-center gap-3 sm:gap-4 mb-6 sm:mb-8">
            <div className="flex-1 h-px bg-gray-200"></div>
            <span className="text-gray-500 text-sm">or choose a template</span>
            <div className="flex-1 h-px bg-gray-200"></div>
          </div>

          {/* Template Images Slider */}
          <div className="space-y-4">
            <h3 className="text-base sm:text-lg text-gray-900 text-center">
              Template Rooms
            </h3>
            <div className="pb-4">
              {isLoading ? (
                <div className="flex justify-center items-center h-48">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                </div>
              ) : isError ? (
                <div className="text-center py-8 text-red-500">
                  Failed to load templates. Please try again.
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                    {templates.map((template: RoomItem) => (
                      <div
                        key={template.id}
                        className="group cursor-pointer"
                        onClick={() => handleSelect(template)}
                      >
                        <Card className="w-full border-gray-200 hover:border-gray-300 hover:shadow-md transition-all duration-200">
                          <CardContent className="p-0">
                            <div className="aspect-[4/3] rounded-t-lg overflow-hidden bg-gray-100">
                              <ImageWithFallback
                                isStorageUrl={true}
                                src={
                                  template.resizedImageUrl || template.imageUrl
                                }
                                alt={template.roomType || 'Room template'}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                              />
                            </div>
                            <div className="p-3 sm:p-4">
                              <h4 className="text-sm text-gray-900 mb-1">
                                {template.roomType || 'Room'}
                              </h4>
                              <Badge
                                variant="secondary"
                                className="text-xs bg-gray-100 text-gray-600"
                              >
                                {template.roomStyle || 'Modern'}
                              </Badge>
                            </div>
                          </CardContent>
                        </Card>
                      </div>
                    ))}
                  </div>
                  {totalPages > 1 && (
                    <div className="mt-4 sm:mt-6 flex justify-center">
                      <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                      />
                    </div>
                  )}
                </>
              )}
            </div>
            <p className="text-center text-xs text-gray-500">
              Click on any template to start your AI room analysis
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShowroomOnboarding;
